import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { validateSite, type ValidationError, PLANS, formatPlanPrice } from '@sarnia/shared';
import { editorStore, useEditorStore } from './editorStore';
import { api } from '../lib/api';
import { Icon } from '../components/ui/Icon';
import './shareDialog.css';

export default function PublishDialog() {
  const {
    site,
    siteId,
    isPublishing,
    publishUrl,
    validationErrors,
    saveState
  } = useEditorStore();
  const [showReport, setShowReport] = useState(false);
  const [copied, setCopied] = useState(false);

  const localReport: ValidationError[] = useMemo(() => {
    if (!site) {
      return [];
    }
    const result = validateSite(site);
    return result.ok ? [] : result.errors;
  }, [site]);

  if (!site) {
    return <div className="sd-empty">No site loaded.</div>;
  }

  const combinedErrors = validationErrors.length > 0 ? validationErrors : localReport;
  const isShared = Boolean(publishUrl);
  const statusLabel = isShared ? 'Preview ready' : 'Draft';

  const copyPublishUrl = async (): Promise<void> => {
    if (!publishUrl) {
      return;
    }
    try {
      await navigator.clipboard.writeText(publishUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // Clipboard is unavailable or blocked; the URL remains visible and selectable.
    }
  };

  return (
    <div className="sd-dialog">
      <header className="sd-dialog__header">
        <p className="sd-dialog__eyebrow">Preview &amp; export</p>
        <h3>Share or take your site with you</h3>
        <p className="sd-dialog__intro">
          Sarnia Builder does not host live websites. Share a clearly marked preview for feedback,
          or download your finished site and host it anywhere.
        </p>
      </header>

      <div className={`sd-status-card ${isShared ? 'sd-status-card--shared' : ''}`}>
        <span
          className={`sd-status-badge ${
            isShared ? 'sd-status-badge--shared' : 'sd-status-badge--draft'
          }`}
        >
          <span className="sd-status-dot" aria-hidden="true" />
          {statusLabel}
        </span>
        {publishUrl ? (
          <div className="sd-status-card__url-row">
            <a className="sd-status-card__url" href={publishUrl} target="_blank" rel="noreferrer">
              {publishUrl}
            </a>
            <button
              type="button"
              className="sd-copy-button"
              onClick={() => void copyPublishUrl()}
              title="Copy preview URL"
              aria-label="Copy preview URL"
            >
              <Icon name={copied ? 'check' : 'copy'} size={15} />
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        ) : (
          <span className="sd-status-card__empty">Not shared yet</span>
        )}
      </div>

      {combinedErrors.length > 0 ? (
        <div className="sd-report">
          <button
            type="button"
            className="sd-report__toggle"
            onClick={() => setShowReport((value) => !value)}
          >
            <Icon name={showReport ? 'chevron-down' : 'chevron-right'} size={14} />
            {showReport ? 'Hide validation report' : 'Show validation report'}
          </button>
          {showReport ? (
            <ul className="sd-report__list">
              {combinedErrors.map((error, index) => (
                <li key={`${error.path}-${index}`}>
                  <span className="sd-report__path">{error.path}:</span> {error.message}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : (
        <p className="sd-ok">No validation problems found.</p>
      )}

      <section className="sd-card" aria-labelledby="sd-share-title">
        <div className="sd-card__icon" aria-hidden="true">
          <Icon name="eye" size={18} />
        </div>
        <div className="sd-card__content">
          <h4 id="sd-share-title">Share a live preview</h4>
          <p className="sd-card__text">
            Send a shareable link marked as a preview for feedback. It is not a hosted website.
          </p>
          <div className="sd-card__actions">
            <button
              type="button"
              className="sarnia-btn sarnia-btn--primary sd-share-button"
              disabled={isPublishing || saveState === 'saving'}
              onClick={() => void editorStore.publish()}
            >
              {isPublishing ? 'Sharing…' : publishUrl ? 'Update preview' : 'Share preview'}
            </button>
            {publishUrl ? (
              <>
                <button
                  type="button"
                  className="sarnia-btn sarnia-btn--secondary sd-stop-button"
                  onClick={() => void editorStore.unpublish()}
                >
                  Stop sharing
                </button>
                <a
                  className="sarnia-btn sarnia-btn--secondary sd-open-button"
                  href={publishUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Icon name="external" size={16} />
                  Open preview
                </a>
              </>
            ) : null}
          </div>
          <details className="sd-details">
            <summary>Preview size</summary>
            <p>Use the device preview in the toolbar to check phone, tablet, and desktop layouts.</p>
          </details>
        </div>
      </section>

      <section className="sd-card" aria-labelledby="sd-export-title">
        <div className="sd-card__icon" aria-hidden="true">
          <Icon name="upload" size={18} />
        </div>
        <div className="sd-card__content">
          <h4 id="sd-export-title">Take your website with you</h4>
          <p className="sd-card__text">Plain HTML, CSS and images. Upload it to any web host.</p>
          <p className="sd-card__helper">Includes a README with hosting steps.</p>
          <a
            className="sarnia-btn sarnia-btn--primary sd-download-button"
            href={api.exportUrl(siteId)}
            download
          >
            <Icon name="arrow-right" size={16} />
            Download website (.zip)
          </a>
        </div>
      </section>

      <section className="sd-card sd-card--highlight" aria-labelledby="sd-go-live-title">
        <div className="sd-card__icon" aria-hidden="true">
          <Icon name="shield" size={18} />
        </div>
        <div className="sd-card__content">
          <h4 id="sd-go-live-title">Want us to handle setup and maintenance?</h4>
          <p className="sd-card__text">
            Everything in the builder is free. If you'd rather we got your site live on your own domain and kept it running, pick a plan.
          </p>
          <div className="sd-plans">
            {PLANS.map((plan) => (
              <div className="sd-plan" key={plan.id}>
                <span className="sd-plan__name">{plan.name}</span>
                <span className="sd-plan__price">
                  {formatPlanPrice(plan)}
                  <span className="sd-plan__interval">/mo</span>
                </span>
              </div>
            ))}
          </div>
          <Link className="sarnia-btn sarnia-btn--secondary sd-plans-link" to="/dashboard/account/billing">
            See plans
          </Link>
        </div>
      </section>
    </div>
  );
}
